import * as THREE from 'three/webgpu';

import { CREATURE as CREATURE_BAKE } from '../bake/BakeConfig.js';
import { OccluderSet } from '../bake/Occluders.js';
import { applyBake, bakeSync } from '../bake/bakeVertexLight.js';
import { mergeNonIndexed, assertNonIndexed, triCount } from '../build/mergeGeo.js';
import { box, cylN, taperedBox } from '../build/prims.js';
import { hexToLinear } from '../../levels/shared/palette.js';
import { tintAlbedo } from '../actors/creatures.js';
import { bakedMaterial } from './materials.js';
import { MissionMarkerBatch } from './MissionMarkers.js';

/**
 * @file `DroneMesh` — the resupply drone's PIXELS. A small quadrotor with an
 * amber crate slung under its belly, plus the amber ring it drops onto the
 * ground beneath itself.
 *
 * This file owns geometry, colour, the rotor spin and the ring. It owns NO
 * game logic: no state machine, no bus, no ammo, no rng, no collider (a flying
 * thing may never register one — `CollisionGrid` is 2D and `heightAt` is one
 * scalar, so a collider under a hovering drone is an invisible wall at ground
 * level). Something else flies it and calls {@link DroneMesh#setPose} each
 * frame.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS FIVE PLAIN MESHES AND NOT A BATCH
 * ─────────────────────────────────────────────────────────────────────────────
 * There is exactly ONE drone in the world, so an `InstancedMesh` would be a
 * per-instance attribute layout, a compaction rule and a lockstep invariant
 * bought for a population of one — and the project's hardest rendering trap
 * lives on that road (a world-space `positionNode` may never ride an
 * `InstancedMesh`, because `NodeMaterial.setupPosition()` multiplies
 * `positionLocal` by `instanceMatrix`). Plain `Mesh`es sidestep the whole
 * question: nothing here builds a vertex in world space, and the scene graph
 * does the transform.
 *
 * The airframe is ONE merged geometry (1 draw). The four rotors are FOUR child
 * meshes SHARING one geometry (4 draws), each parked at an arm tip and each
 * spinning about ITS OWN local Y. ⚠ Do NOT "optimise" the rotors into the
 * airframe merge and spin that: the merged geometry's origin is the drone's
 * centre, so the blades would ORBIT the body instead of spinning in place.
 *
 * ⚠ THAT IS FIVE MESHES AND **SIX DRAWS** — the ground ring's own
 * `MissionMarkerBatch` is the sixth, and it is easy to forget because it is a
 * batch rather than a mesh. `stats().draws` is `1 + rotors + 1` and IS the
 * number; anything in prose that says five is counting the meshes and dropping
 * the ring. Six against this game's ~40–80 is not a budget question either.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ THE LIGHTING TRADE, STATED HONESTLY — THIS IS A FROZEN REST-POSE BAKE
 * ─────────────────────────────────────────────────────────────────────────────
 * `HANDOFF.md`'s brief warns that a moving, turning object cannot use the
 * city's frozen bake and points at cars, which sample the sun LIVE (3 rays
 * along heading + a 0.14 s ramp, riding `iTintD.w`). **This file deliberately
 * takes the simpler, already-shipped trade instead: the one-shot rest-pose
 * bake, exactly as every rig actor in this game does.** A creature's arm bakes
 * once in rest pose and then swings, and it keeps its rest-pose shading; the
 * project makes that trade everywhere it has a rotating part
 * (`actors/creatures.js#buildRig`, step 3).
 *
 * So, plainly: **the drone's shading does not rotate with the drone.** Turn it
 * 180° and the side that was lit is still the side that is lit. That is the
 * rig precedent and not an oversight.
 *
 * What would make someone revisit it, in order of likelihood:
 *
 *  • The drone ends up HOVERING IN PLACE for long stretches beside a wall or
 *    under an awning while the sun moves. A frozen bake cannot darken, so it
 *    would sit lit inside a shadow — the one artefact this trade actually buys.
 *  • It starts making long, slow, LOW passes at a fixed heading, where the eye
 *    has time to notice that the near face never changes.
 *  • Its span grows past ~3 m, at which point it is a vehicle and belongs on
 *    the vehicle path anyway.
 *
 * The fix, if that day comes, is NOT a fourth way: it is the CAR path — an
 * injected sun sampler, a short ramp, and the shade riding a per-instance
 * channel. ⚠ Note the cost that made it wrong here: that channel is
 * PER-INSTANCE, and a plain `Mesh` has no instance data to put it in, so
 * adopting it means adopting the instanced batch this file just argued out of
 * existence. Read `docs/sun.md` and `render/VehicleFx.js` before starting.
 *
 * The frozen bake is also why the material takes ⚠ **`sun: false`**: there is
 * no `color2`/`color3` on this geometry, `vertexColor(n)` falls back to WHITE,
 * and `sun: true` would blow the whole drone out in daylight.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE GROUND RING
 * ─────────────────────────────────────────────────────────────────────────────
 * The drone owns its own ring so its lifetime is nobody else's problem. It is
 * the THIRD `MissionMarkerBatch` in the project (missions and shop doors are
 * the other two) and that repetition is the point: **one shape means one rule,
 * "stand here"**. It is amber to match the crate and the minimap blip, and it
 * is dimmer than a mission's gold so a free resupply never reads as the offer
 * you were sent to take — the same second-colourway reasoning as
 * `Gameplay.js#DOORS`.
 *
 * The ring is also the drone's ANCHOR. There is no shadow: cars deliberately
 * take no blob shadow and `ShadowBlobs` has no plumbing for a thing in the air
 * at all, so without a mark on the tarmac a hovering object has no readable
 * ground position at a 52° pitch. The ring does that job as well as saying
 * "the delivery lands here".
 *
 *  • ⚠ `RENDER_ORDER.marker` is **2.7** and the batch sets it itself. This is a
 *    REUSED row, not a new one — `CLAUDE.md`'s ground-layer table does not gain
 *    a line for the drone, so do not go looking for one.
 *  • ⚠ The tint is LINEAR RGB and ADDITIVE, so what matters against the 0.80
 *    day bloom threshold is `tint * alpha * opacity`, never the tint alone. See
 *    {@link DRONE_ART.ringTint} for the arithmetic.
 *  • ⚠ The batch writes WORLD-space instance matrices, so its object hangs off
 *    `parent` and NOT off the drone's root — under the root it would be
 *    transformed twice and slide away as the drone flew. `parent` must be at
 *    identity, which is the same condition every world-space batch in this
 *    project carries.
 *
 * @module engine/render/DroneMesh
 */

/** @typedef {import('../types.js').Registry} Registry */

const TAU = Math.PI * 2;

/**
 * Every size (metres), colour (sRGB hex, converted through the ONE
 * `hexToLinear`) and cadence the drone is made of.
 */
export const DRONE_ART = Object.freeze({
  /**
   * Rotor-tip to rotor-tip across the diagonal — DERIVED, `2 * (hubR +
   * bladeLen / 2)`. ~1.6 m is the whole sizing decision: it reads as a machine
   * standing beside a 1.3 m chibi without dominating the ~24.8 m the screen
   * spans, and it is small enough that the ring underneath is still visible
   * past it.
   */
  get span() {
    return 2 * (DRONE_ART.hubR + DRONE_ART.bladeLen / 2);
  },

  /** Arm angles, radians. An X-configuration (45°/135°/225°/315°) rather than a
   *  plus: at a fixed 52° pitch a plus puts one arm straight down the view axis
   *  where it is a dot, and the X keeps all four legible from every yaw. */
  armAngles: Object.freeze([Math.PI / 4, (3 * Math.PI) / 4, (5 * Math.PI) / 4, (7 * Math.PI) / 4]),
  /** Distance from the drone axis out to a rotor hub. */
  hubR: 0.52,

  /** The airframe body: a FLATTENED box, tapered on top so the silhouette is
   *  not a cuboid at 20 m. Length runs along +X, which is the nose. */
  body: Object.freeze({ w: 0.46, h: 0.14, d: 0.34, taper: 0.78 }),
  /** A snout at the nose. Its only job is HEADING: a symmetric quadrotor gives
   *  the eye nothing to read `yaw` from, and a delivery drone that faces the
   *  wrong way while it flies looks broken. It protrudes past the body. */
  pod: Object.freeze({ w: 0.11, h: 0.09, d: 0.14, x: 0.245 }),
  /** One arm: long in its own X, thin. Length slightly exceeds `hubR` so the
   *  arm passes THROUGH the hub rather than butting against it — a visible
   *  seam at the joint is the one place a low-poly rig looks unfinished. */
  arm: Object.freeze({ len: 0.54, h: 0.05, w: 0.08 }),
  /** The motor can under each rotor. 6 sides: it is 15 cm across on screen. */
  hub: Object.freeze({ r: 0.075, h: 0.11, y: -0.045, sides: 6 }),

  /** Rotor plane height above the drone origin. Above the hub tops (0.065) so
   *  the disc is clear of the can it sits on. */
  rotorY: 0.085,
  /** ONE bar spanning the full diameter = two blades. */
  bladeLen: 0.56,
  bladeChord: 0.055,
  bladeThick: 0.014,
  /** The spinner cap on top of each rotor, which is what makes a bare bar read
   *  as a rotor rather than as a stick. */
  spinner: Object.freeze({ r: 0.05, h: 0.04, sides: 6 }),

  /** The two struts the crate hangs from. Thin on purpose — the gap between
   *  belly and crate is what says "slung", and a solid block says "fuselage". */
  strut: Object.freeze({ w: 0.035, h: 0.10, d: 0.035, x: 0.11, y: -0.16 }),
  /** THE AMBER CRATE. Big for the airframe it hangs off, deliberately: at 52°
   *  from ~20 m this one saturated block is the entire "this is your ammo"
   *  signal, and everything above it is a grey machine. */
  crate: Object.freeze({ w: 0.34, h: 0.24, d: 0.28, y: -0.40 }),
  /** Two straps wrapping the crate — a girth band and one over the top. They
   *  protrude 10 mm proud of every crate face, so they are real geometry with
   *  a real silhouette and never a coplanar decal fighting for depth. */
  strapBand: Object.freeze({ w: 0.36, h: 0.06, d: 0.30, y: -0.31 }),
  strapOver: Object.freeze({ w: 0.08, h: 0.26, d: 0.30, y: -0.41 }),

  /** Ring radius, metres — the circle the delivery lands in. Bigger than a
   *  shop door's 0.9 m mat (that is a doorstep) and smaller than a mission
   *  objective, because it is a spot to walk to, not a place to be sent. */
  ringRadius: 1.5,
  /**
   * Ring tint, LINEAR RGB, ADDITIVE.
   *
   * ⚠ The number that matters is `tint * alpha * pulse`, not the tint: at
   * {@link DRONE_ART.ringAlpha} 0.78 and `MARKER.pulseMax` 1 the peak is
   * **0.406** on red, which lands just over the 0.80 day bloom threshold once
   * it is added to a lit road — exactly where `MARKER`'s own colours sit, and
   * the halo IS the ring at 40 m.
   *
   * It is 65% of `MARKER.tints.giver`'s 0.62 peak and its green/red ratio is
   * 0.58 against the giver's 0.74, so it separates from a mission on BOTH
   * brightness and hue. ⚠ "Amber vs gold" is a hue shift toward ORANGE, i.e.
   * warmer, not cooler — the two do not read alike because this one is
   * DIMMER and more orange, and a reader checking colour temperature alone
   * will conclude the separation is missing.
   * @type {[number, number, number]}
   */
  ringTint: /** @type {[number, number, number]} */ ([0.52, 0.30, 0.04]),
  /** Per-instance strength before the batch's shared breath. */
  ringAlpha: 0.78,

  /** Authored sRGB. ⚠ Everything here goes through `palette.js#hexToLinear` —
   *  the ONE hex→linear conversion in this project. Raw sRGB fed to the bake is
   *  ~2.2x too bright and invisible until it is on screen. */
  hex: Object.freeze({
    /**
     * Airframe shell AND the four motor cans: light cool slate. It is
     * deliberately LIGHT (0.256/0.300/0.357 linear, ~0.43 once lit) — the first
     * draft was `#5b6570`, which baked to a mean day channel of 0.032 across the
     * whole airframe, and a near-black machine carrying a glowing amber box
     * reads as a floating box with no machine attached.
     */
    shell: '#8b95a1',
    /** Arms, struts and the nose pod: near-black. These are the THIN parts — at
     *  20 m an 8 cm arm is a line, and a line wants to be dark against both the
     *  road and the sky. The motor cans take `shell` instead, so the four
     *  corners of the airframe still read. */
    frame: '#2b2f36',
    /**
     * THE crate. Same amber as the minimap blip (`drone: '#f59e0b'`) — one
     * colour for one thing across the whole game, and one hex rather than a
     * world copy and a HUD copy that drift apart.
     *
     * ⚠ It CLEARS THE DAY BLOOM THRESHOLD and that is deliberate: 0.913 linear
     * on red bakes to a measured **1.109**, so a sunlit crate carries a soft
     * halo.
     *
     * ⚠ AND THERE ARE TWO THRESHOLDS, WHICH IS EASY TO GET WRONG HERE.
     * `render/post.js`'s DEFAULT is **0.80**, but the city — the only level
     * that ships — overrides it to **0.85** (`levels/city/index.js`). 1.109
     * clears BOTH, so the conclusion is unchanged and no margin here depends on
     * which one you quote; but quote the 0.85 when you are talking about the
     * GAME, because a claim measured against the default is a claim about a
     * stage the player never sees. Bloom is the thing this project usually
     * keeps surfaces UNDER (see the `TEX_BOOST` note in `CLAUDE.md`, and the
     * speech bubbles, where bloom on text smears the letterforms) — the
     * exception here is the whole point of the object. A 34 cm box 8 m up has
     * to be findable, the halo is what finds it, and there is no text on it to
     * smear. Darken this hex and you are trading that away, not fixing a bug.
     */
    crate: '#f59e0b',
    /** Strapping: the crate's own hue, darkened, so the bands read as part of
     *  the parcel rather than as a second object. */
    strap: '#6b4410',
    /** Blades: dark, so a spinning disc reads as a disc and not a strobe. */
    blade: '#3a3f47',
    /** Spinner cap: light, four small highlights that tell you it is running. */
    spinner: '#9aa3ad',
  }),
});

export class DroneMesh {
  /**
   * Builds the geometry ONCE. Nothing in this class allocates per frame.
   *
   * @param {Object} o
   * @param {Registry} o.registry Per-level `Registry` — rule 7: everything
   *   created here is registered with it AND released by {@link dispose}.
   * @param {THREE.Object3D} o.parent Scene parent. The root goes under it, and
   *   so does the ring batch — ⚠ which means `parent` must be at IDENTITY, as
   *   for every world-space batch in this project.
   * @param {((x: number, z: number) => number|null)|undefined} [o.heightAt]
   *   Ground sampler for the ring. Absent (or returning a non-finite value) the
   *   ring falls back to y = 0, which is right for a flat dev scene and wrong
   *   on a hillside — inject it in the level.
   * @param {string} [o.name='drone']
   */
  constructor(o) {
    const { registry, parent, heightAt, name = 'drone' } = o ?? {};
    if (!registry) throw new Error('DroneMesh: a Registry is required (rule 7)');
    if (!parent) throw new Error('DroneMesh: a parent Object3D is required');

    this.name = name;
    this._heightAt = typeof heightAt === 'function' ? heightAt : null;
    this._parent = parent;
    this._disposed = false;
    this._visible = false;

    /** Rotor phase, radians. Wrapped every step so it never drifts into the
     *  range where a float32 loses the fractional part. */
    this._spin = 0;
    /** Private clock for the ring's breath — see {@link update}. */
    this._t = 0;
    /** Last pose, kept so {@link update} can place the ring without the caller
     *  passing the position twice. */
    this._x = 0;
    this._y = 0;
    this._z = 0;

    // ── 1. the pieces, in drone-local space ──────────────────────────────────
    // Origin is the AIRFRAME CENTRE: the body straddles y = 0, the rotor plane
    // is just above it and the crate hangs below. `setPose(x, y, z, yaw)`'s `y`
    // is therefore the hover altitude of the airframe, NOT of the crate and NOT
    // of the lowest point — the whole assembly spans about [-0.40, +0.12].
    //
    // ⚠ `prims.js#box` IS BASE-ANCHORED (`y ∈ [0, h]`, centred only in X and Z),
    // so every `y` below is the piece's BOTTOM, never its middle. Reading it as
    // centred is the trap that once put every bridge pier four metres up.
    const A = DRONE_ART;
    const C = {
      shell: hexToLinear(A.hex.shell),
      frame: hexToLinear(A.hex.frame),
      crate: hexToLinear(A.hex.crate),
      strap: hexToLinear(A.hex.strap),
      blade: hexToLinear(A.hex.blade),
      spinner: hexToLinear(A.hex.spinner),
    };

    /** @type {THREE.BufferGeometry[]} Airframe pieces, disposed after merging. */
    const pieces = [];
    /** Occluders for the SELF-shading bake — every piece's world-space AABB,
     *  the rotors included, exactly as `buildRig` feeds itself for inter-part
     *  AO. `OccluderSet#rayT` drops hits at `tmin <= T_MIN`, which is what
     *  stops a piece shading itself into a black slab. */
    const occ = new OccluderSet();
    const m = new THREE.Matrix4();
    const bb = new THREE.Box3();

    /**
     * Tints a piece, places it, records its occluder box and keeps it for the
     * merge. Returns nothing — pieces are anonymous once placed.
     * @param {THREE.BufferGeometry} geo Consumed (owned by `pieces` after this).
     * @param {[number, number, number]} rgb LINEAR albedo.
     * @param {number} x @param {number} y @param {number} z
     * @param {number} [ry] Yaw about the drone axis, radians.
     */
    const put = (geo, rgb, x, y, z, ry = 0) => {
      tintAlbedo(geo, rgb);
      m.makeRotationY(ry);
      m.setPosition(x, y, z);
      geo.applyMatrix4(m);
      geo.computeBoundingBox();
      bb.copy(/** @type {THREE.Box3} */ (geo.boundingBox));
      occ.addBox({ x: bb.min.x, y: bb.min.y, z: bb.min.z }, { x: bb.max.x, y: bb.max.y, z: bb.max.z });
      pieces.push(geo);
    };

    // Body, straddling y = 0.
    put(
      taperedBox(A.body.w, A.body.h, A.body.d, { taperX: A.body.taper, taperZ: A.body.taper }),
      C.shell, 0, -A.body.h / 2, 0,
    );
    // Nose pod, +X is forward (the car convention: `makeRotationY(yaw)` sends
    // local +X to `(cos yaw, 0, -sin yaw)`, which is what `atan2(-fz, fx)` means).
    put(box(A.pod.w, A.pod.h, A.pod.d), C.frame, A.pod.x, -A.pod.h / 2 - 0.01, 0);

    for (const a of A.armAngles) {
      // An arm's own +X must point along `(cos a, 0, sin a)`. `makeRotationY(t)`
      // sends +X to `(cos t, 0, -sin t)`, so the yaw is `-a`, and the box is
      // centred in XZ so it is placed at the arm's MIDPOINT.
      const mid = A.arm.len / 2;
      put(box(A.arm.len, A.arm.h, A.arm.w), C.frame, Math.cos(a) * mid, -A.arm.h / 2, Math.sin(a) * mid, -a);
      // The can takes `shell`, not `frame` — see the palette note. Four light
      // cylinders under four dark discs is what makes the corners read.
      put(
        cylN(A.hub.r, A.hub.h, A.hub.sides),
        C.shell, Math.cos(a) * A.hubR, A.hub.y, Math.sin(a) * A.hubR,
      );
    }

    // The slung crate and its strapping.
    for (const sx of [-A.strut.x, A.strut.x]) {
      put(box(A.strut.w, A.strut.h, A.strut.d), C.frame, sx, A.strut.y, 0);
    }
    put(box(A.crate.w, A.crate.h, A.crate.d), C.crate, 0, A.crate.y, 0);
    put(box(A.strapBand.w, A.strapBand.h, A.strapBand.d), C.strap, 0, A.strapBand.y, 0);
    put(box(A.strapOver.w, A.strapOver.h, A.strapOver.d), C.strap, 0, A.strapOver.y, 0);

    // ── 2. the rotor, ONE geometry shared by four meshes ─────────────────────
    // Built at the ORIGIN so each mesh spins about its own centre, then a COPY
    // is moved out to a tip purely so the bake sees it where it really is.
    const bar = tintAlbedo(box(A.bladeLen, A.bladeThick, A.bladeChord), C.blade);
    bar.translate(0, -A.bladeThick / 2, 0);
    const cap = tintAlbedo(cylN(A.spinner.r, A.spinner.h, A.spinner.sides), C.spinner);
    cap.translate(0, -A.spinner.h / 2, 0);
    const rotorGeo = assertNonIndexed(mergeNonIndexed([bar, cap]), 'DroneMesh.rotor');
    bar.dispose();
    cap.dispose();

    // Every rotor's box goes into the occluder set, so the airframe is shaded
    // by the discs above it.
    for (const a of A.armAngles) {
      const probe = rotorGeo.clone();
      probe.translate(Math.cos(a) * A.hubR, A.rotorY, Math.sin(a) * A.hubR);
      probe.computeBoundingBox();
      bb.copy(/** @type {THREE.Box3} */ (probe.boundingBox));
      occ.addBox({ x: bb.min.x, y: bb.min.y, z: bb.min.z }, { x: bb.max.x, y: bb.max.y, z: bb.max.z });
      probe.dispose();
    }
    occ.build({ cell: 0.5 });

    // ── 3. merge + bake ──────────────────────────────────────────────────────
    const airGeo = assertNonIndexed(mergeNonIndexed(pieces), 'DroneMesh.airframe');
    for (const g of pieces) g.dispose();
    applyBake(airGeo, bakeSync(airGeo, occ, null, CREATURE_BAKE));
    airGeo.computeBoundingBox();
    airGeo.computeBoundingSphere();

    // The rotor is baked at ONE tip and the result is shared by all four, which
    // is the one place this differs from `buildRig`'s per-slot bake. Two reasons
    // it is right here and a left wing's is not: the drone is four-fold
    // symmetric about Y to within the body's 0.46 x 0.34, so the four tips
    // differ by almost nothing to begin with — and the part SPINS. Baking four
    // separate rest-pose shadings for a disc turning tens of radians a second
    // is precision nobody can see, paid for in four geometries.
    //
    // The transplant is `creatures.js`'s: bake a positioned CLONE (positions and
    // normals are what AO and N·L read), carry the two channels back onto the
    // LOCAL geometry. Same vertex order, so the arrays move across directly.
    const a0 = A.armAngles[0];
    const rotorAt = rotorGeo.clone();
    rotorAt.translate(Math.cos(a0) * A.hubR, A.rotorY, Math.sin(a0) * A.hubR);
    applyBake(rotorGeo, bakeSync(rotorAt, occ, null, CREATURE_BAKE));
    rotorAt.dispose();
    rotorGeo.computeBoundingBox();
    rotorGeo.computeBoundingSphere();

    this._airGeo = registry.track(airGeo);
    this._rotorGeo = registry.track(rotorGeo);

    // ── 4. one material for all five meshes ──────────────────────────────────
    // ⚠ `sun: false` — a frozen bake carries no `color2`/`color3`, and
    // `vertexColor(n)` falls back to WHITE, so `sun: true` here blows the whole
    // drone out at noon. ⚠ `vertexColors` stays FALSE inside `bakedMaterial`
    // (the header note in `render/nodes.js`): `colorNode` already reads the
    // attributes, and the flag would multiply by them a second time.
    this._material = registry.material(`${name}:body`, () => bakedMaterial({
      name: `${name}:body`,
      fog: true,
      sun: false,
    }));

    // ── 5. scene graph ───────────────────────────────────────────────────────
    /** @type {THREE.Group} */
    this.root = new THREE.Group();
    this.root.name = name;
    this.root.visible = false;

    const air = new THREE.Mesh(this._airGeo, this._material);
    air.name = `${name}:airframe`;
    this.root.add(air);

    // A real quadrotor counter-rotates adjacent rotors, and the fixed phase
    // offset stops all four lining up into one four-armed shape. Both are one
    // multiply and one add per rotor and neither is load-bearing — but they are
    // stated ONCE here and read by `update`, rather than written at build and
    // again in the step, which is the two-independent-copies trap in miniature.
    /** Per-rotor rest phase, radians. */
    this._rotorPhase = A.armAngles.map((_, i) => (i * Math.PI) / 6);
    /** Per-rotor spin direction, +1 / -1 alternating. */
    this._rotorDir = A.armAngles.map((_, i) => (i % 2 === 0 ? 1 : -1));

    /** @type {THREE.Mesh[]} One per arm tip, in `armAngles` order. */
    this._rotors = A.armAngles.map((a, i) => {
      const mesh = new THREE.Mesh(this._rotorGeo, this._material);
      mesh.name = `${name}:rotor${i}`;
      mesh.position.set(Math.cos(a) * A.hubR, A.rotorY, Math.sin(a) * A.hubR);
      mesh.rotation.y = this._rotorPhase[i];
      this.root.add(mesh);
      return mesh;
    });

    parent.add(this.root);

    // ── 6. the ground ring ───────────────────────────────────────────────────
    // ⚠ Onto `parent`, NOT onto `this.root`: `add()` takes WORLD coordinates and
    // the batch writes world instance matrices, so under the root every ring
    // would be transformed twice and slide off as the drone moved.
    this._marker = new MissionMarkerBatch({ registry, max: 1, name: `${name}:ring` });
    parent.add(this._marker.object);
    this._marker.commit(0); // count 0 => the batch object starts hidden

    // ── 7. the numbers, measured rather than restated ────────────────────────
    const airTris = triCount(this._airGeo);
    const rotorTris = triCount(this._rotorGeo);
    /** Draw calls when drawn: airframe + four rotors + the ring batch. */
    this._draws = 1 + this._rotors.length + 1;
    /** Triangles when drawn; the ring is one quad. */
    this._tris = airTris + this._rotors.length * rotorTris + 2;
  }

  /**
   * World pose. Call every frame the drone moves.
   *
   * @param {number} x @param {number} y Airframe CENTRE, not the crate and not
   *   the lowest point — see the constructor's note. @param {number} z
   * @param {number} yaw Heading of travel, radians, the same convention as a
   *   car's: `+X` is the nose, so `yaw = atan2(-fz, fx)` for a heading `f`.
   */
  setPose(x, y, z, yaw) {
    if (this._disposed) return;
    this._x = x;
    this._y = y;
    this._z = z;
    this.root.position.set(x, y, z);
    this.root.rotation.y = yaw;
  }

  /**
   * Advances the rotor spin and rebuilds the ground ring.
   *
   * ⚠ **Call this EVERY frame, not only while the drone is visible.** The ring
   * batch's breath is a function of a clock, and the mission and door rings ride
   * `manager.elapsed`; this object's clock is the sum of the `dt`s it was
   * handed, so skipping frames puts its pulse out of phase with the other two
   * and three rings in one view breathing out of step read as three unrelated
   * effects. The hidden path is a `reset()` + `commit()` and costs nothing.
   *
   * @param {number} dt Seconds.
   * @param {number} spin Rotor rate, rad/s, handed in by the caller so there is
   *   not a second copy of the number here. ⚠ It is a LEGIBILITY figure, not a
   *   physical one: a true rotor rate aliases at 60 fps into a slow backwards
   *   crawl (and at the perf governor's 30 FPS cap, into a stopped rotor), so
   *   something in the tens of rad/s is what actually reads as "running".
   */
  update(dt, spin) {
    if (this._disposed) return;
    const step = Number.isFinite(dt) ? dt : 0;
    const rate = Number.isFinite(spin) ? spin : 0;
    this._t += step;
    this._spin = (this._spin + rate * step) % TAU;
    for (let i = 0; i < this._rotors.length; i++) {
      this._rotors[i].rotation.y = this._rotorPhase[i] + this._spin * this._rotorDir[i];
    }

    // Per-frame rebuild, the `MissionMarkerBatch` contract: reset → add → commit.
    // Nothing re-added stops being drawn, which is what makes hiding free.
    this._marker.reset();
    if (this._visible) {
      const g = this._heightAt ? this._heightAt(this._x, this._z) : null;
      const y = Number.isFinite(g) ? /** @type {number} */ (g) : 0;
      this._marker.add(this._x, y, this._z, DRONE_ART.ringRadius, DRONE_ART.ringTint, DRONE_ART.ringAlpha);
    }
    this._marker.commit(this._t);
  }

  /**
   * Shows or hides the whole object — the drone is IDLE and nowhere for most of
   * a session, and an idle drone must cost nothing on screen. Hiding takes the
   * ring with it in the SAME call rather than waiting for the next
   * {@link update}, or a drone dismissed mid-frame leaves its circle on the
   * pavement.
   *
   * @param {boolean} v
   */
  setVisible(v) {
    if (this._disposed) return;
    this._visible = !!v;
    this.root.visible = this._visible;
    if (!this._visible) {
      this._marker.reset();
      this._marker.commit(this._t);
    }
  }

  /**
   * Cost when drawn, plus whether it is being paid. `draws`/`tris` are the
   * SUBMITTED cost of a visible drone (they do not fall to zero when hidden —
   * `visible` is the field that says that), and both are measured off the real
   * geometry at build rather than restated from {@link DRONE_ART}.
   *
   * @returns {{draws: number, tris: number, visible: boolean}}
   */
  stats() {
    return { draws: this._draws, tris: this._tris, visible: this._visible };
  }

  /** Idempotent (rule 7). */
  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    this._visible = false;
    this._marker.dispose();
    this.root.removeFromParent();
    for (const r of this._rotors) r.removeFromParent();
    this._rotors.length = 0;
    this._airGeo.dispose();
    this._rotorGeo.dispose();
    this._material.dispose();
  }
}
